import React from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SourceIcon from "@mui/icons-material/Source";
import ReactMarkdown from "react-markdown";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../agent-response.module.scss";

type SupplierDiscoveryQnAData = {
  agent_id: string;
  agent_name: string;
  question: string;
  answer: string;
  intent: string;
  sources: string[];
  confidence: number | null;
};

type SupplierDiscoveryQnAProps = {
  parsedContent: any;
};

export default function SupplierDiscoveryQnA({
  parsedContent,
}: SupplierDiscoveryQnAProps): JSX.Element {
  const finalAnswer = parsedContent?.finalAnswer || {};
  const outputData: SupplierDiscoveryQnAData = safeJSONParse(
    finalAnswer?.output || "{}",
  );

  const { question, answer, sources } = outputData;

  return (
    <Box className={styles.pressReleaseContent}>
      {/* Question Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          backgroundColor: "#f8fffe",
          border: "1px solid #00d2aa",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <QuestionAnswerIcon
            sx={{ fontSize: 20, color: "#00d2aa", mt: 0.2 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "#666", display: "block", mb: 0.5 }}
            >
              Your Question
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#004141", fontWeight: 500 }}
            >
              {question}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Answer Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            "& p": { mb: 2, lineHeight: 1.7, color: "#333" },
            "& ul": { pl: 3, mb: 2 },
            "& li": { mb: 1, lineHeight: 1.6, color: "#333" },
            "& strong": { color: "#004141", fontWeight: 600 },
            "& em": { fontStyle: "italic" },
            "& code": {
              backgroundColor: "#f5f5f5",
              padding: "2px 6px",
              borderRadius: 1,
              fontSize: "0.9em",
              fontFamily: "monospace",
            },
          }}
        >
          <ReactMarkdown>{answer}</ReactMarkdown>
        </Box>

        {/* Sources */}
        {sources && sources.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <SourceIcon sx={{ fontSize: 18, color: "#666" }} />
              <Typography
                variant="caption"
                sx={{ color: "#666", fontWeight: 600 }}
              >
                Sources Referenced
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {sources.map((source, idx) => (
                <Chip
                  key={idx}
                  label={source}
                  size="small"
                  sx={{
                    backgroundColor: "#f0f7ff",
                    color: "#1565c0",
                    fontSize: "12px",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
